import { ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { I18nPluralPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { map } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatOptionModule } from '@angular/material/core';
import { SidenavContentModule } from '../../../shared/sidenav/sidenav-content/sidenav-content.module';
import { AvatarBlockModule } from '../../../shared/avatar-block/avatar-block.module';
import { EmptyStateModule } from '../../../shared/empty-state/empty-state.module';
import { ControlMessagesModule } from '../../../../shared/control-messages/control-messages.module';
import { SidenavContentRef } from '../../../shared/sidenav/sidenav-content/sidenav-content-ref';
import { GrowlerService } from '../../../../../services/growler.service';
import { SIDENAV_CONTENT_DATA } from '../../../shared/sidenav/sidenav-content/sidenav-content.service';
import { Membership } from '../../../../../models/entities/membership';
import { MembersService } from '../../../../../services/members/members.service';
import { I18nMap } from '../../../../../models/entities/i18n-map';
import { IsTypeofDirective } from '../../../shared/directives/typeof.directive';
import { ToggleListComponent, ToggleListGroupDirective } from '../../../shared/toggle-list/toggle-list.component';
import { MobileButtonPipe } from '../../../../../pipes/format/mobile-button.pipe';
import { ButtonSpacerDirective } from '../../../shared/directives/button.directive';

export interface MemberAdminSidenavInput {
    title: string;
    required: boolean;
    requireEmail?: boolean;

    // preselected ids
    selectedMembershipIds?: number[];

    // scope membership to organization
    orgId?: number;
    // callout homeowner(s) via unitId
    unitId?: number;

    // provide confirmation button label with string or i18n-mapped object
    confirmButton?: string|I18nMap;

    // provide text to display when no memberships are found onload
    emptyStateLabel?: string;
}

export interface MemberAdminSidenavOutput {
    success: true;
    selectedMemberships: Membership[];
}

@UntilDestroy()
@Component({
    selector: 'app-member-admin-sidenav',
    templateUrl: './member-admin-sidenav.component.html',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        SidenavContentModule,
        MatButtonModule,
        MatSlideToggleModule,
        MatCardModule,
        AvatarBlockModule,
        EmptyStateModule,
        ControlMessagesModule,
        MatOptionModule,
        I18nPluralPipe,
        IsTypeofDirective,
        ToggleListComponent,
        ToggleListGroupDirective,
        MobileButtonPipe,
        ButtonSpacerDirective,
    ],
})
export class MemberAdminSidenavComponent implements OnInit
{
    @ViewChild(ToggleListComponent<Membership>) listRef: ToggleListComponent<Membership>;

    memberships: Membership[];
    groups: { [prop: string]: (membership: Membership) => boolean } = {};

    readonly confirmLabelMapping: I18nMap = {
        '=0': 'Select Members',
        '=1': 'Select 1 Member',
        other: 'Select # Members',
    };

    constructor(
            private _membersService: MembersService,
            private _sidenavRef: SidenavContentRef<MemberAdminSidenavComponent>,
            private _growler: GrowlerService,
            @Inject(SIDENAV_CONTENT_DATA) public data: MemberAdminSidenavInput,
    ) {
    }

    readonly adminMemberships = (membership: Membership) => !!membership.isAdmin;
    readonly ownerMemberships = (membership: Membership) => {
        if (!this.data.unitId) {
            return false;
        }

        return membership.owners.filter((owner) => owner.unitId === this.data.unitId).length > 0;
    };
    readonly disabledMemberships = (membership: Membership) => {
        return this.data.requireEmail && !membership.user?.email;
    };

    ngOnInit() {
        const options: { [prop: string]: string|boolean } = {
            status: 'active',
            owners: true,
        };

        this._membersService.getMembers((this.data.orgId || null), options).pipe(
            map((res) => (res['memberships'] as any[]).map((membership: any) => new Membership(membership))),
            map((memberships) => {
                return memberships.sort((a, b) => a.name.localeCompare(b.name));
            }),
            untilDestroyed(this),
        ).subscribe({
            next: (res) => {
                this.memberships = res;
            },
            error: () => this._growler.oops('Unable to get requested members'),
        });
    }

    confirmSelection() {
        if (this.listRef.validate) {
            const output: MemberAdminSidenavOutput = {
                success: true,
                selectedMemberships: this.listRef.combinedValue,
            };

            this._sidenavRef.close(output);
        }
    }
}
